import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LayoutDashboard, Package, ShoppingCart, Users, IndianRupee, Plus, Loader2, Trophy, Globe, Trash2, Ticket, FileSpreadsheet, Monitor, Download, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, fmt, errMsg, ORDER_STYLES, mediaUrl } from "../lib/api";
import { useAuth } from "../context/Store";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import AdminHomeContent from "../components/AdminHomeContent";

const TABS = [
  ["overview", "Overview", LayoutDashboard],
  ["homepage", "Home Page", Monitor],
  ["products", "Products", Package],
  ["orders", "Orders", ShoppingCart],
  ["members", "Members", Users],
  ["kyc", "KYC", ShieldCheck],
  ["partners", "Partners", Users],
  ["rankshare", "RankShare", Trophy],
  ["regenx", "ReGenX", Globe],
  ["coupons", "Coupons", Ticket],
  ["reports", "Reports", FileSpreadsheet],
  ["withdrawals", "Withdrawals", IndianRupee],
];

const EMPTY_PRODUCT = { name: "", category: "Electronics", brand: "Vearn", price: "", mrp: "", tax_rate: 18, stock: 0, image: "", images: [], description: "", featured: false, partner_eligible: false, activation_eligible: false, repurchase_eligible: false, rv_value: 0, status: "published" };
const EMPTY_RANK = { name: "", target: "", weaker_pct: 30, shares: "" };
const EMPTY_COUPON = { code: "", value: "", min_purchase: 3000, only_partner_products: true, usage_limit: "", expires_at: "" };

function Card({ label, value, testid }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-brand-surface/60 p-6">
      <p className="text-[11px] uppercase tracking-[0.2em] text-white/40 font-semibold">{label}</p>
      <p className="font-display text-3xl font-bold text-white mt-2" data-testid={testid}>{value}</p>
    </div>
  );
}

const darkInput = "mt-1.5 w-full h-10 rounded-xl bg-white/5 border border-white/10 px-3 text-sm text-white outline-none focus:ring-2 focus:ring-brand-cyan placeholder:text-white/25";
const th = "px-5 py-3.5 text-left text-xs uppercase tracking-wider text-white/40 font-semibold";
const td = "px-5 py-4 text-sm text-white/70";

function prevMonth() {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export default function Admin() {
  const { user, startMemberView } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [partners, setPartners] = useState([]);
  const [members, setMembers] = useState([]);
  const [kycRecords, setKycRecords] = useState([]);
  const [kycDialog, setKycDialog] = useState(null);
  const [kycReviewNote, setKycReviewNote] = useState("");
  const [memberDialog, setMemberDialog] = useState(null);
  const [memberForm, setMemberForm] = useState({ email: "", phone: "", password: "" });
  const [withdrawals, setWithdrawals] = useState([]);
  const [rsData, setRsData] = useState(null);
  const [rgxData, setRgxData] = useState(null);
  const [rgxLevel, setRgxLevel] = useState(null);
  const [rgxLevelData, setRgxLevelData] = useState({ nodes: [], total: 0, skip: 0, limit: 100 });
  const [rgxLevelLoading, setRgxLevelLoading] = useState(false);
  const [coupons, setCoupons] = useState([]);
  const [couponForm, setCouponForm] = useState(EMPTY_COUPON);
  const [repFrom, setRepFrom] = useState("");
  const [repTo, setRepTo] = useState("");
  const [rankForm, setRankForm] = useState(EMPTY_RANK);
  const [dialog, setDialog] = useState(null);
  const [form, setForm] = useState(EMPTY_PRODUCT);
  const [imageFiles, setImageFiles] = useState([]);
  const [imageUrlInput, setImageUrlInput] = useState("");
  const imageFilesRef = useRef(imageFiles);
  imageFilesRef.current = imageFiles;
  const [payDialog, setPayDialog] = useState(null);
  const [transferRef, setTransferRef] = useState("");
  const [exportingWithdrawals, setExportingWithdrawals] = useState(false);
  const [withdrawalDate, setWithdrawalDate] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => () => imageFilesRef.current.forEach((item) => URL.revokeObjectURL(item.preview)), []);

  const loadWithdrawals = (date = withdrawalDate) => {
    api.get("/admin/withdrawals", date ? { params: { date } } : {}).then((r) => setWithdrawals(r.data.withdrawals)).catch(() => {});
  };

  const loadRgxLevel = (level, skip = 0) => {
    setRgxLevelLoading(true);
    api.get(`/admin/regenx/levels/${level}`, { params: { skip, limit: 100 } })
      .then((r) => setRgxLevelData(r.data))
      .catch((err) => toast.error(errMsg(err)))
      .finally(() => setRgxLevelLoading(false));
  };

  const load = () => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/products").then((r) => setProducts(r.data.products)).catch(() => {});
    api.get("/admin/orders").then((r) => setOrders(r.data.orders)).catch(() => {});
    api.get("/admin/partners").then((r) => setPartners(r.data.partners)).catch(() => {});
    api.get("/admin/users").then((r) => setMembers(r.data.users)).catch(() => {});
    api.get("/admin/kyc").then((r) => setKycRecords(r.data.records)).catch(() => {});
    loadWithdrawals();
    api.get("/admin/rankshare").then((r) => setRsData(r.data)).catch(() => {});
    api.get("/admin/regenx").then((r) => {
      setRgxData(r.data);
      if (r.data.placement_result?.placed_count) {
        toast.success(`Placed ${r.data.placement_result.placed_count} active Partner ID(s) by activation seniority.`);
      }
      const levels = r.data.levels || [];
      if (levels.length) {
        const selectedLevel = levels.some((item) => item.level === rgxLevel)
          ? rgxLevel
          : levels[0].level;
        setRgxLevel(selectedLevel);
        loadRgxLevel(selectedLevel);
      } else {
        setRgxLevel(null);
        setRgxLevelData({ nodes: [], total: 0, skip: 0, limit: 100 });
      }
    }).catch(() => {});
    api.get("/admin/coupons").then((r) => setCoupons(r.data.coupons)).catch(() => {});
  };

  useEffect(() => {
    if (user === null) navigate("/auth?next=/admin");
    if (user && user.role !== "admin") navigate("/");
    if (user?.role === "admin") load();
  }, [user]); // eslint-disable-line

  if (!user || user.role !== "admin") return null;

  const act = async (fn, msg) => {
    try {
      await fn();
      toast.success(msg);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const saveMember = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { email: memberForm.email, phone: memberForm.phone };
      if (memberForm.password) payload.password = memberForm.password;
      await api.patch(`/admin/users/${memberDialog.id}`, payload);
      toast.success("Member details updated");
      setMemberDialog(null);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const changeMemberStatus = (member) => act(
    () => api.patch(`/admin/users/${member.id}/status`, { account_status: member.account_status === "deactivated" ? "active" : "deactivated" }),
    member.account_status === "deactivated" ? "Member account activated" : "Member account deactivated",
  );

  const deleteMember = (member) => {
    const confirmed = window.confirm(
      `Permanently delete ${member.name || member.email} and their local account, orders/payment records, partner earnings, withdrawals, KYC, and profile data? This is blocked if they have any active Partner in their genealogy. Cashfree gateway records cannot be deleted from this site.`,
    );
    if (!confirmed) return;
    act(() => api.delete(`/admin/users/${member.id}`), "Member and local records permanently deleted");
  };

  const reconcileBinaryIncome = async (partner) => {
    if (!window.confirm(`Reverse all remaining binary earnings for ${partner.partner_id}? This is available only when one or both active genealogy legs have zero members. If earnings were already withdrawn, their wallet may become negative until offset by future income.`)) return;
    try {
      const response = await api.post(`/admin/partners/${partner.partner_id}/reconcile-binary`);
      toast.success(`Reversed ${fmt(response.data.reversed_amount || 0)} in stale binary income`);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const resendPartnerWelcome = async (member) => {
    try {
      const { data } = await api.post(`/admin/users/${member.id}/resend-partner-welcome`);
      toast.info(`Welcome delivery for ${member.partner_id}: email ${data.email_status}; SMS ${data.sms_status}.`);
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const activateMemberPartner = (member) => act(
    () => api.post(`/admin/users/${member.id}/activate-partner`),
    "Partner ID activated and placed in ReGenX when a sponsor is available; admin-activated branches do not count for binary pairs",
  );

  const viewMember = async (member) => {
    try {
      await startMemberView(member.id);
      navigate("/account");
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const submitKycReview = async (e) => {
    e.preventDefault();
    if (!kycDialog) return;
    setBusy(true);
    try {
      await api.post(`/admin/kyc/${kycDialog.user_id}/review`, {
        status: kycDialog.decision,
        review_note: kycReviewNote.trim() || null,
      });
      toast.success(kycDialog.decision === "approved" ? "PAN KYC approved" : "PAN KYC rejected");
      setKycDialog(null);
      setKycReviewNote("");
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const downloadWithdrawalSheet = async () => {
    setExportingWithdrawals(true);
    try {
      const response = await api.get("/admin/withdrawals/bulk-payment.xlsx", {
        responseType: "blob",
        params: withdrawalDate ? { date: withdrawalDate } : {},
      });
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      const filename = response.headers["content-disposition"]?.match(/filename="?([^";]+)"?/i)?.[1] || "BLKPAY.xlsx";
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast.success("Bank payment Excel downloaded");
    } catch (err) {
      let message = errMsg(err);
      if (err.response?.data instanceof Blob) {
        try {
          const body = JSON.parse(await err.response.data.text());
          message = body.detail || message;
        } catch { /* use the standard request error */ }
      }
      toast.error(message);
    } finally {
      setExportingWithdrawals(false);
    }
  };

  const saveProduct = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = { ...form, price: Number(form.price), mrp: Number(form.mrp), tax_rate: Number(form.tax_rate), stock: Number(form.stock), rv_value: Number(form.rv_value) || 0 };
    try {
      const uploadedImages = await Promise.all(imageFiles.map(async ({ file }) => {
        const imageData = new FormData();
        imageData.append("image", file);
        const uploaded = await api.post("/admin/upload-image", imageData);
        return uploaded.data.image_url;
      }));
      payload.images = [...(form.images || []), ...uploadedImages].slice(0, 12);
      payload.image = payload.images[0] || "";
      if (dialog === "new") await api.post("/admin/products", payload);
      else await api.put(`/admin/products/${dialog.id}`, payload);
      toast.success("Product saved");
      setDialog(null);
      imageFiles.forEach((item) => URL.revokeObjectURL(item.preview));
      setImageFiles([]);
      setImageUrlInput("");
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const saveRank = async (e) => {
    e.preventDefault();
    await act(async () => {
      await api.post("/admin/rankshare/ranks", { ...rankForm, target: Number(rankForm.target), weaker_pct: Number(rankForm.weaker_pct), shares: Number(rankForm.shares) });
      setRankForm(EMPTY_RANK);
    }, "Rank added");
  };

  const saveCoupon = async (e) => {
    e.preventDefault();
    await act(async () => {
      await api.post("/admin/coupons", {
        code: couponForm.code, value: Number(couponForm.value),
        min_purchase: Number(couponForm.min_purchase) || 0,
        only_partner_products: couponForm.only_partner_products,
        usage_limit: couponForm.usage_limit ? Number(couponForm.usage_limit) : null,
        expires_at: couponForm.expires_at ? new Date(couponForm.expires_at).toISOString() : null,
      });
      setCouponForm(EMPTY_COUPON);
    }, "Coupon created");
  };

  return (
    <div className="min-h-screen bg-brand-navy text-white pt-24 md:pt-28 pb-24" data-testid="admin-panel">
      <div className="mx-auto w-full max-w-[1600px] px-4 sm:px-6 lg:px-8">
        <p className="text-xs uppercase tracking-[0.3em] text-brand-cyan font-semibold">Vearn Command Center</p>
        <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight mt-3">Admin Panel</h1>

        <div className="mt-8 flex gap-2 overflow-x-auto pb-1">
          {TABS.map(([key, label, Icon]) => (
            <button key={key} data-testid={`admin-tab-${key}`} onClick={() => setTab(key)}
              className={`shrink-0 flex items-center gap-2 rounded-full px-5 h-10 text-sm font-semibold transition-colors ${tab === key ? "brand-gradient-bg text-white" : "bg-white/5 text-white/60 hover:bg-white/10"}`}>
              <Icon className="h-4 w-4" /> {label}
            </button>
          ))}
        </div>

        <div className="mt-8">
          {tab === "homepage" && <AdminHomeContent />}

          {tab === "overview" && stats && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6" data-testid="admin-stats">
              <Card label="Revenue" value={fmt(stats.revenue)} testid="stat-revenue" />
              <Card label="Orders" value={stats.orders} testid="stat-orders" />
              <Card label="Customers" value={stats.customers} testid="stat-customers" />
              <Card label="Active Partners" value={stats.partners} testid="stat-partners" />
              <Card label="Pending orders" value={stats.pending_orders} testid="stat-pending-orders" />
              <Card label="Pending withdrawals" value={stats.pending_withdrawals} testid="stat-pending-withdrawals" />
              <Card label="Partner income paid" value={fmt(stats.income_distributed)} testid="stat-income" />
              <Card label="Low stock alerts" value={stats.low_stock} testid="stat-low-stock" />
            </div>
          )}

          {tab === "products" && (
            <div>
              <button data-testid="add-product-button" onClick={() => { setForm(EMPTY_PRODUCT); setImageFiles([]); setImageUrlInput(""); setDialog("new"); }}
                className="mb-6 flex items-center gap-2 rounded-full brand-gradient-bg px-6 h-11 text-sm font-semibold">
                <Plus className="h-4 w-4" /> New product
              </button>
              <div className="rounded-2xl border border-white/10 bg-brand-surface/40 overflow-x-auto" data-testid="admin-products-table">
                <table className="w-full min-w-[860px]">
                  <thead><tr className="border-b border-white/10">
                    <th className={th}>Product</th><th className={th}>Category</th><th className={th}>Price</th><th className={th}>RV</th><th className={th}>Stock</th><th className={th}>Flags</th><th className={th}>Status</th><th className={th}></th>
                  </tr></thead>
                  <tbody>
                    {products.map((p) => (
                      <tr key={p.id} className="border-b border-white/5" data-testid={`admin-product-${p.slug}`}>
                        <td className={td}><div className="flex items-center gap-3">{p.images?.[0] && <img src={mediaUrl(p.images[0])} alt="" className="h-10 w-10 rounded-lg object-cover" />}<span className="font-medium text-white">{p.name}</span></div></td>
                        <td className={td}>{p.category}</td>
                        <td className={td}>{fmt(p.price)}</td>
                        <td className={td}>{p.repurchase_eligible ? <span className="text-brand-cyan">{fmt(p.rv_value || 0)}</span> : "—"}</td>
                        <td className={td}><span className={p.stock < 10 ? "text-amber-400 font-semibold" : ""}>{p.stock}</span></td>
                        <td className={td}>
                          <div className="flex gap-1.5">
                            {p.activation_eligible && <span className="rounded-full bg-brand-cyan/15 text-brand-cyan px-2.5 py-0.5 text-[10px] font-bold">ACTIVATION</span>}
                            {p.featured && <span className="rounded-full bg-brand-blue/20 text-blue-300 px-2.5 py-0.5 text-[10px] font-bold">FEATURED</span>}
                          </div>
                        </td>
                        <td className={td}><span className={`rounded-full px-3 py-1 text-xs font-bold ${p.status === "published" ? "bg-emerald-400/15 text-emerald-400" : "bg-white/10 text-white/40"}`}>{p.status}</span></td>
                        <td className={td}>
                          <div className="flex gap-2">
                            <button data-testid={`edit-product-${p.slug}`} onClick={() => { const images = p.images?.length ? p.images : (p.image ? [p.image] : []); setForm({ ...EMPTY_PRODUCT, ...p, images, image: images[0] || "" }); setImageFiles([]); setImageUrlInput(""); setDialog(p); }}
                              className="rounded-lg bg-white/10 px-3 py-1.5 text-xs font-semibold hover:bg-white/20">Edit</button>
                            {p.status !== "archived" && (
                              <button data-testid={`${p.status === "published" ? "disable" : "enable"}-product-${p.slug}`}
                                onClick={() => act(() => api.patch(`/admin/products/${p.id}/status`, { status: p.status === "published" ? "disabled" : "published" }), p.status === "published" ? "Product disabled" : "Product enabled")}
                                className={`rounded-lg px-3 py-1.5 text-xs font-semibold ${p.status === "published" ? "bg-amber-500/15 text-amber-300 hover:bg-amber-500/25" : "bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/25"}`}>
                                {p.status === "published" ? "Disable" : "Enable"}
                              </button>
                            )}
                            {p.status !== "archived" && (
                              <button onClick={() => act(() => api.delete(`/admin/products/${p.id}`), "Product archived")}
                                className="rounded-lg bg-rose-500/15 text-rose-400 px-3 py-1.5 text-xs font-semibold hover:bg-rose-500/25">Archive</button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {tab === "orders" && (
            <div className="rounded-2xl border border-white/10 bg-brand-surface/40 overflow-x-auto" data-testid="admin-orders-table">
              <table className="w-full min-w-[760px]">
                <thead><tr className="border-b border-white/10">
                  <th className={th}>Order</th><th className={th}>Date</th><th className={th}>Items</th><th className={th}>Total</th><th className={th}>Status</th><th className={th}>Actions</th>
                </tr></thead>
                <tbody>
                  {orders.length === 0 && <tr><td colSpan={6} className="px-6 py-12 text-center text-white/30">No successfully paid orders yet.</td></tr>}
                  {orders.map((o) => (
                    <tr key={o.order_id} className="border-b border-white/5" data-testid={`admin-order-${o.order_id}`}>
                      <td className={td}><span className="font-mono text-xs text-brand-cyan">{o.order_id}</span>{o.coupon_code && <span className="block text-[10px] text-brand-cyan/60 mt-0.5">{o.coupon_code} −{fmt(o.discount || 0)}</span>}</td>
                      <td className={td}>{new Date(o.created_at).toLocaleDateString("en-IN")}</td>
                      <td className={td}>{o.items.map((i) => `${i.name} ×${i.qty}`).join(", ").slice(0, 50)}</td>
                      <td className={td}>{fmt(o.total)}</td>
                      <td className={td}>
                        <span className={`rounded-full px-3 py-1 text-xs font-bold ${ORDER_STYLES[o.status] || "bg-white/10"}`}>{o.status}</span>
                        {o.inventory_state === "shortfall" && <span className="mt-2 block max-w-48 text-xs text-amber-300">Stock issue: {o.inventory_issue || "Restock the item, then retry."}</span>}
                      </td>
                      <td className={td}>
                        <div className="flex gap-2 items-center">
                          {o.status === "CREATED" && (
                            <button data-testid={`mark-paid-${o.order_id}`} onClick={() => act(() => api.post(`/admin/orders/${o.order_id}/mark-paid`), "Order marked paid")}
                              className="rounded-lg bg-emerald-500/15 text-emerald-400 px-3 py-1.5 text-xs font-semibold hover:bg-emerald-500/25">Mark paid</button>
                          )}
                          {o.status === "PAID" && o.inventory_state === "shortfall" && (
                            <button data-testid={`retry-inventory-${o.order_id}`} onClick={() => act(() => api.post(`/admin/orders/${o.order_id}/mark-paid`), "Inventory checked again")}
                              className="rounded-lg bg-amber-500/15 text-amber-300 px-3 py-1.5 text-xs font-semibold hover:bg-amber-500/25">Retry stock check</button>
                          )}
                          {["PAID", "PROCESSING", "SHIPPED"].includes(o.status) && (
                            <select data-testid={`order-status-select-${o.order_id}`} value={o.status}
                              onChange={(e) => act(() => api.patch(`/admin/orders/${o.order_id}/status`, { status: e.target.value }), "Status updated")}
                              className="rounded-lg bg-white/10 px-2 py-1.5 text-xs text-white outline-none">
                              {["PAID", "PROCESSING", "SHIPPED", "DELIVERED"].map((s) => <option key={s} value={s} className="text-black">{s}</option>)}
                            </select>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {tab === "members" && (
            <div className="rounded-2xl border border-white/10 bg-brand-surface/40 overflow-x-auto" data-testid="admin-members-table">
              <table className="w-full min-w-[1320px] table-fixed">
                <colgroup><col className="w-[18%]" /><col className="w-[23%]" /><col className="w-[13%]" /><col className="w-[11%]" /><col className="w-[13%]" /><col className="w-[22%]" /></colgroup>
                <thead><tr className="border-b border-white/10">
                  <th className={th}>Member</th><th className={th}>Email</th><th className={th}>Phone</th><th className={th}>Account</th><th className={th}>Partner ID</th><th className={th}>Actions</th>
                </tr></thead>
                <tbody>
                  {members.length === 0 && <tr><td colSpan={6} className="px-6 py-12 text-center text-white/30">No customer accounts found.</td></tr>}
                  {members.map((member) => (
                    <tr key={member.id} className="border-b border-white/5" data-testid={`admin-member-${member.id}`}>
                      <td className={td}><span className="block truncate font-medium text-white" title={member.name}>{member.name || "—"}</span></td>
                      <td className={`${td} truncate`} title={member.email}>{member.email}</td>
                      <td className={`${td} whitespace-nowrap`}>{member.phone || "—"}</td>
                      <td className={`${td} whitespace-nowrap`}><span className={`rounded-full px-3 py-1 text-xs font-bold ${member.account_status === "deactivated" ? "bg-rose-400/15 text-rose-400" : "bg-emerald-400/15 text-emerald-400"}`}>{member.account_status === "deactivated" ? "Deactivated" : "Active"}</span></td>
                      <td className={`${td} whitespace-nowrap`}><span className="font-mono text-xs text-brand-cyan">{member.partner_id || "Customer"}</span></td>
                      <td className={td}>
                        <div className="grid grid-cols-2 gap-2">
                          <button data-testid={`view-member-${member.id}`} onClick={() => viewMember(member)} className="whitespace-nowrap rounded-lg bg-white/10 px-2.5 py-2 text-xs font-semibold hover:bg-white/20">View member</button>
                          <button onClick={() => { setMemberDialog(member); setMemberForm({ email: member.email || "", phone: member.phone || "", password: "" }); }} className="rounded-lg bg-white/10 px-2.5 py-2 text-xs font-semibold hover:bg-white/20">Edit</button>
                          <button onClick={() => changeMemberStatus(member)} className={`whitespace-nowrap rounded-lg px-2.5 py-2 text-xs font-semibold ${member.account_status === "deactivated" ? "bg-emerald-500/15 text-emerald-400" : "bg-rose-500/15 text-rose-400"}`}>{member.account_status === "deactivated" ? "Activate" : "Deactivate"}</button>
                          <button data-testid={`delete-member-${member.id}`} onClick={() => deleteMember(member)} className="whitespace-nowrap rounded-lg bg-rose-500/15 px-2.5 py-2 text-xs font-semibold text-rose-400 hover:bg-rose-500/25">Delete</button>
                          {member.partner_id && <button onClick={() => resendPartnerWelcome(member)} className="col-span-2 rounded-lg bg-brand-cyan/10 px-2.5 py-2 text-xs font-semibold text-brand-cyan hover:bg-brand-cyan/20">Resend welcome email + SMS</button>}
                          {!member.partner_id && member.account_status !== "deactivated" && <button onClick={() => activateMemberPartner(member)} className="col-span-2 rounded-lg brand-gradient-bg px-2.5 py-2 text-xs font-semibold">Activate Partner</button>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {tab === "kyc" && (
            <div className="rounded-2xl border border-white/10 bg-brand-surface/40 overflow-x-auto" data-testid="admin-kyc-table">
              <div className="border-b border-white/10 px-5 py-4">
                <h2 className="font-display text-xl font-bold">PAN KYC reviews</h2>
                <p className="mt-1 text-xs text-white/45">PAN numbers are visible to admins here for manual verification. Only approved KYC members can withdraw.</p>
              </div>
              <table className="w-full min-w-[900px]">
                <thead><tr className="border-b border-white/10">
                  <th className={th}>Member</th><th className={th}>Email</th><th className={th}>PAN</th><th className={th}>Submitted</th><th className={th}>Status</th><th className={th}>Review</th>
                </tr></thead>
                <tbody>
                  {kycRecords.length === 0 && <tr><td colSpan={6} className="px-6 py-12 text-center text-white/30">No PAN KYC submissions yet.</td></tr>}
                  {kycRecords.map((record) => (
                    <tr key={record.user_id} className="border-b border-white/5" data-testid={`admin-kyc-${record.user_id}`}>
                      <td className={td}><span className="font-medium text-white">{record.name}</span></td>
                      <td className={td}>{record.email}</td>
                      <td className={`${td} font-mono tracking-wider text-brand-cyan`}>{record.pan_number}</td>
                      <td className={td}>{record.submitted_at ? new Date(record.submitted_at).toLocaleString("en-IN") : "—"}</td>
                      <td className={td}><span className={`rounded-full px-3 py-1 text-xs font-bold ${record.status === "approved" ? "bg-emerald-400/15 text-emerald-400" : record.status === "rejected" ? "bg-rose-400/15 text-rose-400" : "bg-amber-400/15 text-amber-300"}`}>{record.status}</span>{record.review_note && <span className="mt-1 block max-w-56 text-xs text-white/40">{record.review_note}</span>}</td>
                      <td className={td}>
                        {record.status === "pending" ? <div className="flex gap-2">
                          <button onClick={() => { setKycDialog({ ...record, decision: "approved" }); setKycReviewNote(""); }} className="rounded-lg bg-emerald-500/15 px-3 py-1.5 text-xs font-semibold text-emerald-400">Approve</button>
                          <button onClick={() => { setKycDialog({ ...record, decision: "rejected" }); setKycReviewNote(""); }} className="rounded-lg bg-rose-500/15 px-3 py-1.5 text-xs font-semibold text-rose-400">Reject</button>
                        </div> : <span className="text-xs text-white/35">Reviewed {record.reviewed_at ? new Date(record.reviewed_at).toLocaleDateString("en-IN") : "—"}</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {tab === "partners" && (
            <div className="rounded-2xl border border-white/10 bg-brand-surface/40 overflow-x-auto" data-testid="admin-partners-table">
              <table className="w-full min-w-[820px]">
                <thead><tr className="border-b border-white/10">
                  <th className={th}>Partner ID</th><th className={th}>Name</th><th className={th}>Code</th><th className={th}>Sponsor</th><th className={th}>L / R</th><th className={th}>Wallet</th><th className={th}>Earned</th><th className={th}>Actions</th>
                </tr></thead>
                <tbody>
                  {partners.length === 0 && <tr><td colSpan={8} className="px-6 py-12 text-center text-white/30">No partners yet.</td></tr>}
                  {partners.map((p) => (
                    <tr key={p.partner_id} className="border-b border-white/5" data-testid={`admin-partner-${p.partner_id}`}>
                      <td className={td}><span className="font-mono text-brand-cyan text-xs">{p.partner_id}</span></td>
                      <td className={td}><span className="text-white font-medium">{p.name}</span></td>
                      <td className={td}><span className="font-mono text-xs">{p.referral_code}</span></td>
                      <td className={td}><span className="font-mono text-xs">{p.sponsor_partner_id || "—"}</span></td>
                      <td className={td}>{p.left_count} / {p.right_count}</td>
                      <td className={td}>{fmt(p.wallet_balance)}</td>
                      <td className={td}>{fmt(p.total_earned)}</td>
                      <td className={td}>{(!p.left_count || !p.right_count) && <button onClick={() => reconcileBinaryIncome(p)} className="whitespace-nowrap rounded-lg bg-rose-500/15 px-3 py-2 text-xs font-semibold text-rose-300 hover:bg-rose-500/25">Reverse stale binary</button>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {tab === "rankshare" && rsData && (
            <div data-testid="admin-rankshare">
              <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
                <h2 className="font-display text-2xl font-bold">Monthly pools</h2>
                <button data-testid="run-distribution-button"
                  onClick={() => act(() => api.post("/admin/rankshare/distribute", { month: prevMonth() }).then((r) => toast.info(r.data.distributed ? `Paid ${fmt(r.data.paid_out)} to ${r.data.qualified} partner(s)` : `Not distributed: ${r.data.reason}`)), "Distribution run complete")}
                  className="rounded-full brand-gradient-bg px-6 h-10 text-sm font-semibold">
                  Finalise {prevMonth()} pool
                </button>
              </div>
              <div className="rounded-2xl border border-white/10 bg-brand-surface/40 overflow-x-auto mb-10">
                <table className="w-full min-w-[640px]">
                  <thead><tr className="border-b border-white/10">
                    <th className={th}>Month</th><th className={th}>Pool</th><th className={th}>Kit sales</th><th className={th}>Status</th><th className={th}>Paid out</th>
                  </tr></thead>
                  <tbody>
                    {rsData.pools.length === 0 && <tr><td colSpan={5} className="px-6 py-12 text-center text-white/30">No pool activity yet — ₹50 accrues per eligible kit sale.</td></tr>}
                    {rsData.pools.map((p) => (
                      <tr key={p.month} className="border-b border-white/5" data-testid={`pool-${p.month}`}>
                        <td className={td}><span className="font-mono text-brand-cyan">{p.month}</span>{p.month === rsData.current_month && <span className="ml-2 text-[10px] text-white/30">(current)</span>}</td>
                        <td className={td}>{fmt(p.pool)}</td>
                        <td className={td}>{p.kits}</td>
                        <td className={td}><span className={`rounded-full px-3 py-1 text-xs font-bold ${p.status === "distributed" ? "bg-emerald-400/15 text-emerald-400" : p.status === "rolled" ? "bg-amber-400/15 text-amber-400" : "bg-brand-blue/20 text-blue-300"}`}>{p.status}</span></td>
                        <td className={td}>{p.paid_out ? `${fmt(p.paid_out)} (${p.qualified_count} partners, ${fmt(p.per_share)}/share)` : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <h2 className="font-display text-2xl font-bold mb-6">Ranks</h2>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 rounded-2xl border border-white/10 bg-brand-surface/40 overflow-x-auto">
                  <table className="w-full min-w-[520px]">
                    <thead><tr className="border-b border-white/10">
                      <th className={th}>Rank</th><th className={th}>Target (kits)</th><th className={th}>Weaker leg</th><th className={th}>Shares</th><th className={th}></th>
                    </tr></thead>
                    <tbody>
                      {rsData.ranks.map((r) => (
                        <tr key={r.id} className="border-b border-white/5" data-testid={`admin-rank-${r.name.toLowerCase()}`}>
                          <td className={td}><span className="font-semibold text-white">{r.name}</span></td>
                          <td className={td}>{r.target}</td>
                          <td className={td}>≥ {r.weaker_pct}%</td>
                          <td className={td}>{r.shares}</td>
                          <td className={td}><button onClick={() => act(() => api.delete(`/admin/rankshare/ranks/${r.id}`), "Rank removed")} className="p-1.5 text-white/30 hover:text-rose-400"><Trash2 className="h-4 w-4" /></button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <form onSubmit={saveRank} className="rounded-2xl border border-white/10 bg-brand-surface/40 p-6 h-fit" data-testid="rank-form">
                  <h3 className="font-display text-lg font-bold mb-4">Add rank</h3>
                  <div className="space-y-3">
                    <div><label className="text-xs font-semibold text-white/50 uppercase tracking-wider">Name</label>
                      <input data-testid="rank-name" required value={rankForm.name} onChange={(e) => setRankForm({ ...rankForm, name: e.target.value })} placeholder="Diamond" className={darkInput} /></div>
                    <div><label className="text-xs font-semibold text-white/50 uppercase tracking-wider">Monthly team kits target</label>
                      <input data-testid="rank-target" type="number" min={1} required value={rankForm.target} onChange={(e) => setRankForm({ ...rankForm, target: e.target.value })} className={darkInput} /></div>
                    <div><label className="text-xs font-semibold text-white/50 uppercase tracking-wider">Weaker leg min %</label>
                      <input data-testid="rank-weaker" type="number" min={0} max={100} required value={rankForm.weaker_pct} onChange={(e) => setRankForm({ ...rankForm, weaker_pct: e.target.value })} className={darkInput} /></div>
                    <div><label className="text-xs font-semibold text-white/50 uppercase tracking-wider">Shares</label>
                      <input data-testid="rank-shares" type="number" min={1} required value={rankForm.shares} onChange={(e) => setRankForm({ ...rankForm, shares: e.target.value })} className={darkInput} /></div>
                    <button type="submit" data-testid="rank-save" className="w-full h-10 rounded-full brand-gradient-bg text-white text-sm font-semibold">Save rank</button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {tab === "regenx" && rgxData && (
            <div data-testid="admin-regenx">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
                <Card label="Global IDs" value={rgxData.total_nodes} testid="rgx-total-nodes" />
                <Card label="Rebirth IDs" value={rgxData.rebirth_nodes} testid="rgx-rebirth-nodes" />
                <Card label="Income distributed" value={fmt(rgxData.total_distributed)} testid="rgx-distributed" />
                <Card label="Wallets outstanding" value={fmt(rgxData.wallets_outstanding)} testid="rgx-outstanding" />
              </div>
              <h2 className="font-display text-2xl font-bold mt-10 mb-3">Global placement by level</h2>
              <p className="text-sm text-white/45 mb-5">Opening ReGenX places any missing active Partner IDs oldest activation first, following the normal tree and income rules. Select a level to inspect IDs, owners, parents, and positions.</p>
              <div className="flex flex-wrap gap-2 mb-5">
                {(rgxData.levels || []).map((item) => (
                  <button key={item.level} type="button" onClick={() => {
                    setRgxLevel(item.level);
                    loadRgxLevel(item.level);
                  }} className={`rounded-xl border px-4 py-2 text-sm font-semibold transition ${rgxLevel === item.level ? "border-brand-cyan bg-brand-cyan/15 text-brand-cyan" : "border-white/10 bg-brand-surface/40 text-white/60 hover:border-white/25"}`}>
                    Level {item.level} <span className="ml-1 text-xs opacity-70">({item.count})</span>
                  </button>
                ))}
                {(rgxData.levels || []).length === 0 && <p className="text-sm text-white/35">No ReGenX placements yet.</p>}
              </div>
              {rgxLevel !== null && <>
                <div className="mb-3 flex items-center justify-between gap-4">
                  <h3 className="font-semibold">Level {rgxLevel} placements <span className="text-sm font-normal text-white/40">({rgxLevelData.total})</span></h3>
                  {rgxLevelLoading && <Loader2 className="h-4 w-4 animate-spin text-brand-cyan" />}
                </div>
                <div className="rounded-2xl border border-white/10 bg-brand-surface/40 overflow-x-auto">
                  <table className="w-full min-w-[900px]">
                    <thead><tr className="border-b border-white/10">
                      <th className={th}>Global ID</th><th className={th}>Owner</th><th className={th}>Partner ID</th><th className={th}>Parent ID</th><th className={th}>Position</th><th className={th}>Source</th><th className={th}>Created</th>
                    </tr></thead>
                    <tbody>
                      {rgxLevelData.nodes.length === 0 && <tr><td colSpan={7} className="px-6 py-12 text-center text-white/30">No IDs at this level.</td></tr>}
                      {rgxLevelData.nodes.map((n) => (
                        <tr key={n.gid} className="border-b border-white/5">
                          <td className={td}><span className="font-mono text-brand-cyan text-xs">{n.gid}</span></td>
                          <td className={td}>{n.owner_name}</td>
                          <td className={`${td} font-mono text-xs`}>{n.partner_id || "—"}</td>
                          <td className={`${td} font-mono text-xs`}>{n.parent_gid || "Root"}</td>
                          <td className={td}>{n.position}</td>
                          <td className={td}><span className={`rounded-full px-3 py-1 text-xs font-bold ${n.source === "rebirth" ? "bg-brand-blue/20 text-blue-300" : "bg-brand-cyan/15 text-brand-cyan"}`}>{n.source === "admin_activation" ? "Admin activation" : n.source}</span></td>
                          <td className={td}>{new Date(n.created_at).toLocaleDateString("en-IN")}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="mt-4 flex items-center justify-between text-sm text-white/50">
                  <span>{rgxLevelData.total ? `${rgxLevelData.skip + 1}–${Math.min(rgxLevelData.skip + rgxLevelData.nodes.length, rgxLevelData.total)} of ${rgxLevelData.total}` : "0 IDs"}</span>
                  <div className="flex gap-2">
                    <button type="button" disabled={rgxLevelLoading || rgxLevelData.skip <= 0}
                      onClick={() => loadRgxLevel(rgxLevel, Math.max(0, rgxLevelData.skip - rgxLevelData.limit))}
                      className="rounded-lg border border-white/10 px-3 py-1.5 disabled:opacity-30">Previous</button>
                    <button type="button" disabled={rgxLevelLoading || rgxLevelData.skip + rgxLevelData.limit >= rgxLevelData.total}
                      onClick={() => loadRgxLevel(rgxLevel, rgxLevelData.skip + rgxLevelData.limit)}
                      className="rounded-lg border border-white/10 px-3 py-1.5 disabled:opacity-30">Next</button>
                  </div>
                </div>
              </>}
            </div>
          )}

          {tab === "coupons" && (
            <div data-testid="admin-coupons">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 rounded-2xl border border-white/10 bg-brand-surface/40 overflow-x-auto">
                  <table className="w-full min-w-[620px]">
                    <thead><tr className="border-b border-white/10">
                      <th className={th}>Code</th><th className={th}>Value</th><th className={th}>Min purchase</th><th className={th}>Scope</th><th className={th}>Used</th><th className={th}>Status</th><th className={th}></th>
                    </tr></thead>
                    <tbody>
                      {coupons.length === 0 && <tr><td colSpan={7} className="px-6 py-12 text-center text-white/30">No coupons yet.</td></tr>}
                      {coupons.map((c) => (
                        <tr key={c.id} className="border-b border-white/5" data-testid={`coupon-${c.code}`}>
                          <td className={td}><span className="font-mono font-bold text-brand-cyan">{c.code}</span></td>
                          <td className={td}>{fmt(c.value)}</td>
                          <td className={td}>{c.min_purchase ? fmt(c.min_purchase) : "—"}</td>
                          <td className={td}><span className="text-xs">{c.only_partner_products ? "Partner kits only" : "All products"}</span></td>
                          <td className={td}>{c.used_count}{c.usage_limit ? ` / ${c.usage_limit}` : ""}</td>
                          <td className={td}><span className={`rounded-full px-3 py-1 text-xs font-bold ${c.active ? "bg-emerald-400/15 text-emerald-400" : "bg-white/10 text-white/40"}`}>{c.active ? "active" : "inactive"}</span></td>
                          <td className={td}>{c.active && <button data-testid={`deactivate-${c.code}`} onClick={() => act(() => api.post(`/admin/coupons/${c.id}/deactivate`), "Coupon deactivated")} className="rounded-lg bg-rose-500/15 text-rose-400 px-3 py-1.5 text-xs font-semibold">Deactivate</button>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <form onSubmit={saveCoupon} className="rounded-2xl border border-white/10 bg-brand-surface/40 p-6 h-fit" data-testid="coupon-form">
                  <h3 className="font-display text-lg font-bold mb-4">New coupon</h3>
                  <div className="space-y-3">
                    <div><label className="text-xs font-semibold text-white/50 uppercase tracking-wider">Code</label>
                      <input data-testid="cf-code" required value={couponForm.code} onChange={(e) => setCouponForm({ ...couponForm, code: e.target.value.toUpperCase() })} placeholder="PARTNER1000" className={darkInput} /></div>
                    <div><label className="text-xs font-semibold text-white/50 uppercase tracking-wider">Discount (₹ flat)</label>
                      <input data-testid="cf-value" type="number" min={1} required value={couponForm.value} onChange={(e) => setCouponForm({ ...couponForm, value: e.target.value })} placeholder="1000" className={darkInput} /></div>
                    <div><label className="text-xs font-semibold text-white/50 uppercase tracking-wider">Min purchase (₹)</label>
                      <input data-testid="cf-min" type="number" min={0} value={couponForm.min_purchase} onChange={(e) => setCouponForm({ ...couponForm, min_purchase: e.target.value })} className={darkInput} /></div>
                    <div><label className="text-xs font-semibold text-white/50 uppercase tracking-wider">Usage limit (blank = unlimited)</label>
                      <input data-testid="cf-limit" type="number" min={1} value={couponForm.usage_limit} onChange={(e) => setCouponForm({ ...couponForm, usage_limit: e.target.value })} className={darkInput} /></div>
                    <div><label className="text-xs font-semibold text-white/50 uppercase tracking-wider">Expiry date (optional)</label>
                      <input data-testid="cf-expiry" type="date" value={couponForm.expires_at} onChange={(e) => setCouponForm({ ...couponForm, expires_at: e.target.value })} className={darkInput} /></div>
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <input type="checkbox" data-testid="cf-partner-only" checked={couponForm.only_partner_products} onChange={(e) => setCouponForm({ ...couponForm, only_partner_products: e.target.checked })} className="h-4 w-4 accent-[#00E5FF]" />
                      Partner kits only
                    </label>
                    <button type="submit" data-testid="cf-save" className="w-full h-10 rounded-full brand-gradient-bg text-white text-sm font-semibold">Create coupon</button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {tab === "reports" && (
            <div data-testid="admin-reports">
              <div className="rounded-2xl border border-white/10 bg-brand-surface/40 p-6 mb-6 flex flex-wrap items-end gap-4">
                <div><label className="text-xs font-semibold text-white/50 uppercase tracking-wider">From date</label>
                  <input data-testid="report-from" type="date" value={repFrom} onChange={(e) => setRepFrom(e.target.value)} className={darkInput + " w-44"} /></div>
                <div><label className="text-xs font-semibold text-white/50 uppercase tracking-wider">To date</label>
                  <input data-testid="report-to" type="date" value={repTo} onChange={(e) => setRepTo(e.target.value)} className={darkInput + " w-44"} /></div>
                <p className="text-xs text-white/30 pb-2">Leave blank for all-time reports.</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {[
                  { key: "sales", title: "Sales report", desc: "Every order with customer, items, discounts, coupons, totals and status.", testid: "download-sales" },
                  { key: "income", title: "Partner income report", desc: "Direct, binary, RankShare and repurchase income credited to every partner.", testid: "download-income" },
                  { key: "payouts", title: "Payouts report", desc: "All withdrawal requests with charges, rebirth splits, status and bank references.", testid: "download-payouts" },
                ].map((r) => (
                  <div key={r.key} className="rounded-2xl border border-white/10 bg-brand-surface/40 p-7 flex flex-col">
                    <FileSpreadsheet className="h-7 w-7 text-brand-cyan" />
                    <h3 className="font-display text-xl font-bold mt-4">{r.title}</h3>
                    <p className="text-sm text-white/50 mt-2 leading-relaxed flex-1">{r.desc}</p>
                    <button
                      data-testid={r.testid}
                      onClick={() => {
                        const q = `${repFrom ? `from_=${repFrom}` : ""}${repFrom && repTo ? "&" : ""}${repTo ? `to=${repTo}` : ""}`;
                        window.open(`${process.env.REACT_APP_BACKEND_URL}/api/admin/reports/${r.key}.xlsx${q ? `?${q}` : ""}`, "_blank");
                      }}
                      className="mt-6 h-11 rounded-full brand-gradient-bg text-white text-sm font-semibold">
                      Download Excel
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {tab === "withdrawals" && (
            <div data-testid="admin-withdrawals-page">
              <div className="mb-5 flex flex-wrap items-end justify-between gap-4">
                <div>
                  <h2 className="font-display text-2xl font-bold">Withdrawal requests</h2>
                  <p className="mt-1 text-sm text-white/45">{withdrawalDate ? `Showing ${withdrawals.length} request${withdrawals.length === 1 ? "" : "s"} from ${new Date(`${withdrawalDate}T00:00:00`).toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata" })} (IST).` : `Showing ${withdrawals.length} request${withdrawals.length === 1 ? "" : "s"} from all dates.`} Approve requests before export.</p>
                </div>
                <div className="flex flex-wrap items-end gap-3">
                  <label className="text-xs font-semibold text-white/50">
                    Filter by date (IST)
                    <input data-testid="withdrawal-date-filter" type="date" value={withdrawalDate} onChange={(e) => {
                      const date = e.target.value;
                      setWithdrawalDate(date);
                      loadWithdrawals(date);
                    }} className={darkInput + " mt-1 block w-48 [color-scheme:dark]"} />
                  </label>
                  {withdrawalDate && <button type="button" onClick={() => { setWithdrawalDate(""); loadWithdrawals(""); }} className="h-10 rounded-full bg-white/10 px-4 text-sm font-semibold hover:bg-white/15">Clear date</button>}
                  <button data-testid="download-blkpay-withdrawals" onClick={downloadWithdrawalSheet} disabled={exportingWithdrawals}
                    className="flex h-11 items-center justify-center gap-2 rounded-full brand-gradient-bg px-5 text-sm font-semibold disabled:opacity-60">
                    {exportingWithdrawals ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                    {exportingWithdrawals ? "Preparing Excel…" : "Download bank payment Excel"}
                  </button>
                </div>
              </div>
              <div className="rounded-2xl border border-white/10 bg-brand-surface/40 overflow-x-auto" data-testid="admin-withdrawals-table">
              <table className="w-full min-w-[900px]">
                <thead><tr className="border-b border-white/10">
                  <th className={th}>Request</th><th className={th}>Type</th><th className={th}>Partner</th><th className={th}>Gross</th><th className={th}>Net</th><th className={th}>Bank</th><th className={th}>Status</th><th className={th}>Actions</th>
                </tr></thead>
                <tbody>
                  {withdrawals.length === 0 && <tr><td colSpan={8} className="px-6 py-12 text-center text-white/30">{withdrawalDate ? "No withdrawal requests for this date." : "No withdrawal requests."}</td></tr>}
                  {withdrawals.map((w) => (
                    <tr key={w.request_id} className="border-b border-white/5" data-testid={`admin-wd-${w.request_id}`}>
                      <td className={td}><span className="font-mono text-xs text-brand-cyan">{w.request_id}</span></td>
                      <td className={td}><span className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold ${w.wallet_type === "regenx" ? "bg-brand-blue/20 text-blue-300" : "bg-brand-cyan/15 text-brand-cyan"}`}>{(w.wallet_type || "partner").toUpperCase()}</span></td>
                      <td className={td}>{w.name}<br /><span className="text-xs text-white/30 font-mono">{w.partner_id || ""}</span></td>
                      <td className={td}>{fmt(w.gross)}</td>
                      <td className={td + " text-emerald-400"}>{fmt(w.net)}{w.rebirth_amount ? <span className="block text-[10px] text-white/30">+{fmt(w.rebirth_amount)} rebirth</span> : null}</td>
                      <td className={td}><span className="text-xs">{w.bank.holder}<br />{w.bank.account_number_masked || w.bank.account_number} · {w.bank.ifsc}</span></td>
                      <td className={td}><span className={`rounded-full px-3 py-1 text-xs font-bold ${w.status === "paid" ? "bg-emerald-400/15 text-emerald-400" : w.status === "rejected" ? "bg-rose-400/15 text-rose-400" : w.status === "approved" ? "bg-brand-blue/20 text-blue-300" : "bg-amber-400/15 text-amber-400"}`}>{w.status}</span></td>
                      <td className={td}>
                        <div className="flex gap-2">
                          {w.status === "pending" && (
                            <>
                              <button data-testid={`approve-${w.request_id}`} disabled={!w.bank_details_complete} title={!w.bank_details_complete ? "Reject and ask the member to submit new bank details" : undefined} onClick={() => act(() => api.post(`/admin/withdrawals/${w.request_id}/approve`), "Approved")}
                                className="rounded-lg bg-emerald-500/15 text-emerald-400 px-3 py-1.5 text-xs font-semibold disabled:cursor-not-allowed disabled:opacity-40">{w.bank_details_complete ? "Approve" : "Missing bank details"}</button>
                              <button data-testid={`reject-${w.request_id}`} onClick={() => act(() => api.post(`/admin/withdrawals/${w.request_id}/reject`, {}), "Rejected & refunded")}
                                className="rounded-lg bg-rose-500/15 text-rose-400 px-3 py-1.5 text-xs font-semibold">Reject</button>
                            </>
                          )}
                          {w.status === "approved" && (
                            <button data-testid={`pay-${w.request_id}`} onClick={() => { setPayDialog(w); setTransferRef(""); }}
                              className="rounded-lg brand-gradient-bg px-3 py-1.5 text-xs font-semibold">Mark paid</button>
                          )}
                          {w.transfer_ref && <span className="text-xs text-white/30">{w.transfer_ref}</span>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            </div>
          )}
        </div>
      </div>

      <Dialog open={!!dialog} onOpenChange={() => setDialog(null)}>
        <DialogContent className="bg-brand-navy border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-xl">{dialog === "new" ? "New product" : "Edit product"}</DialogTitle></DialogHeader>
          <form onSubmit={saveProduct} className="grid grid-cols-2 gap-4 mt-4" data-testid="product-form">
            <div className="col-span-2"><label className="text-xs font-semibold text-white/50 uppercase tracking-wider">Name</label>
              <input data-testid="pf-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={darkInput} /></div>
            <div><label className="text-xs font-semibold text-white/50 uppercase tracking-wider">Category</label>
              <input data-testid="pf-category" required value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={darkInput} /></div>
            <div><label className="text-xs font-semibold text-white/50 uppercase tracking-wider">Brand</label>
              <input data-testid="pf-brand" value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} className={darkInput} /></div>
            <div><label className="text-xs font-semibold text-white/50 uppercase tracking-wider">Price (₹)</label>
              <input data-testid="pf-price" type="number" required min={1} value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className={darkInput} /></div>
            <div><label className="text-xs font-semibold text-white/50 uppercase tracking-wider">MRP (₹)</label>
              <input data-testid="pf-mrp" type="number" required min={1} value={form.mrp} onChange={(e) => setForm({ ...form, mrp: e.target.value })} className={darkInput} /></div>
            <div><label className="text-xs font-semibold text-white/50 uppercase tracking-wider">GST %</label>
              <input data-testid="pf-tax" type="number" min={0} max={28} value={form.tax_rate} onChange={(e) => setForm({ ...form, tax_rate: e.target.value })} className={darkInput} /></div>
            <div><label className="text-xs font-semibold text-white/50 uppercase tracking-wider">Stock</label>
              <input data-testid="pf-stock" type="number" min={0} value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} className={darkInput} /></div>
            <div className="col-span-2 space-y-3">
              <label className="text-xs font-semibold text-white/50 uppercase tracking-wider">Product images (up to 12)</label>
              <input data-testid="pf-image-upload" type="file" multiple accept="image/png,image/jpeg,image/webp" onChange={(e) => {
                const files = Array.from(e.target.files || []).slice(0, Math.max(0, 12 - (form.images?.length || 0) - imageFiles.length));
                setImageFiles((current) => [...current, ...files].map((file) => file instanceof File ? { file, preview: URL.createObjectURL(file) } : file));
                e.target.value = "";
              }} className="block w-full text-sm text-white/60 file:mr-4 file:rounded-full file:border-0 file:bg-brand-blue/20 file:px-4 file:py-2 file:font-semibold file:text-brand-cyan hover:file:bg-brand-blue/30" />
              <p className="text-xs text-white/35">Select several JPG, PNG, or WebP images. Each file can be up to 8 MB. Swipe through them on product cards and the product page.</p>
              <div className="flex flex-wrap gap-3">
                {(form.images || []).map((image, index) => <div key={`${image}-${index}`} className="relative">
                  <img src={mediaUrl(image)} alt={`Product image ${index + 1}`} className="h-20 w-20 rounded-xl border border-white/10 object-cover" />
                  <button type="button" onClick={() => setForm({ ...form, images: form.images.filter((_, imageIndex) => imageIndex !== index), image: form.images.filter((_, imageIndex) => imageIndex !== index)[0] || "" })} className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-rose-600 text-white" aria-label={`Remove image ${index + 1}`}>×</button>
                </div>)}
                {imageFiles.map((item, index) => <div key={`${item.file.name}-${index}`} className="relative">
                  <img src={item.preview} alt={`New product image ${index + 1}`} className="h-20 w-20 rounded-xl border border-white/10 object-cover" />
                  <button type="button" onClick={() => { URL.revokeObjectURL(item.preview); setImageFiles((current) => current.filter((_, fileIndex) => fileIndex !== index)); }} className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-rose-600 text-white" aria-label={`Remove selected image ${index + 1}`}>×</button>
                </div>)}
              </div>
              <label htmlFor="pf-image-url" className="block text-[11px] font-semibold text-white/40 uppercase tracking-wider">Add an image URL</label>
              <div className="flex gap-2">
                <input id="pf-image-url" data-testid="pf-image" value={imageUrlInput} onChange={(e) => setImageUrlInput(e.target.value)} placeholder="https://..." className={darkInput + " mt-0"} />
                <button type="button" disabled={!imageUrlInput.trim() || (form.images?.length || 0) + imageFiles.length >= 12} onClick={() => { const images = [...(form.images || []), imageUrlInput.trim()]; setForm({ ...form, images, image: images[0] || "" }); setImageUrlInput(""); }} className="shrink-0 rounded-xl bg-white/10 px-4 text-sm font-semibold text-white disabled:opacity-40">Add URL</button>
              </div>
            </div>
            <div className="col-span-2"><label className="text-xs font-semibold text-white/50 uppercase tracking-wider">Description</label>
              <textarea data-testid="pf-description" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={darkInput + " h-auto py-3"} /></div>
            <div><label className="text-xs font-semibold text-white/50 uppercase tracking-wider">RV value (₹ per unit)</label>
              <input data-testid="pf-rv" type="number" min={0} value={form.rv_value} onChange={(e) => setForm({ ...form, rv_value: e.target.value })} className={darkInput} /></div>
            <div className="col-span-2 flex flex-wrap gap-5 text-sm">
              {[["featured", "Featured"], ["partner_eligible", "Partner eligible"], ["activation_eligible", "Activates Partner ID"], ["repurchase_eligible", "RV repurchase eligible"]].map(([key, label]) => (
                <label key={key} className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" data-testid={`pf-${key}`} checked={!!form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.checked })} className="h-4 w-4 accent-[#00E5FF]" />
                  {label}
                </label>
              ))}
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" data-testid="pf-status" checked={form.status === "published"} onChange={(e) => setForm({ ...form, status: e.target.checked ? "published" : "draft" })} className="h-4 w-4 accent-[#00E5FF]" />
                Published
              </label>
            </div>
            <button type="submit" disabled={busy} data-testid="pf-save"
              className="col-span-2 h-12 rounded-full brand-gradient-bg text-white font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-60">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} Save product
            </button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!payDialog} onOpenChange={() => setPayDialog(null)}>
        <DialogContent className="bg-brand-navy border-white/10 text-white max-w-md">
          <DialogHeader><DialogTitle className="font-display text-xl">Confirm bank transfer</DialogTitle></DialogHeader>
          <p className="text-sm text-white/50">Pay {payDialog && fmt(payDialog.net)} to {payDialog?.bank.holder} ({payDialog?.bank.account_number} · {payDialog?.bank.ifsc}), then enter the bank reference.</p>
          <input data-testid="transfer-ref-input" value={transferRef} onChange={(e) => setTransferRef(e.target.value)} placeholder="UTR / transaction reference" className={darkInput + " mt-4"} />
          <button data-testid="confirm-paid-button" disabled={transferRef.length < 4}
            onClick={() => act(async () => { await api.post(`/admin/withdrawals/${payDialog.request_id}/pay`, { transfer_ref: transferRef }); setPayDialog(null); }, "Marked as paid")}
            className="mt-4 w-full h-11 rounded-full brand-gradient-bg text-white font-semibold text-sm disabled:opacity-40">
            Mark as paid
          </button>
        </DialogContent>
      </Dialog>

      <Dialog open={!!memberDialog} onOpenChange={() => setMemberDialog(null)}>
        <DialogContent className="bg-brand-navy border-white/10 text-white max-w-md">
          <DialogHeader><DialogTitle className="font-display text-xl">Edit member</DialogTitle></DialogHeader>
          <form onSubmit={saveMember} className="space-y-4 mt-3">
            <div><label className="text-xs font-semibold text-white/50 uppercase tracking-wider">Email</label>
              <input type="email" required value={memberForm.email} onChange={(e) => setMemberForm({ ...memberForm, email: e.target.value })} className={darkInput} /></div>
            <div><label className="text-xs font-semibold text-white/50 uppercase tracking-wider">Mobile number</label>
              <input type="tel" required value={memberForm.phone} onChange={(e) => setMemberForm({ ...memberForm, phone: e.target.value })} className={darkInput} /></div>
            <div><label className="text-xs font-semibold text-white/50 uppercase tracking-wider">Set a new password</label>
              <input type="password" minLength={6} maxLength={100} autoComplete="new-password" value={memberForm.password} onChange={(e) => setMemberForm({ ...memberForm, password: e.target.value })} placeholder="Leave blank to keep current password" className={darkInput} /></div>
            <button type="submit" disabled={busy} className="w-full h-11 rounded-full brand-gradient-bg text-sm font-semibold disabled:opacity-50">Save member</button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!kycDialog} onOpenChange={() => setKycDialog(null)}>
        <DialogContent className="bg-brand-navy border-white/10 text-white max-w-md">
          <DialogHeader><DialogTitle className="font-display text-xl">{kycDialog?.decision === "approved" ? "Approve PAN KYC" : "Reject PAN KYC"}</DialogTitle></DialogHeader>
          <div className="mt-3 rounded-xl bg-white/5 p-4 text-sm">
            <p className="text-white/50">Member <span className="font-semibold text-white">{kycDialog?.name}</span></p>
            <p className="mt-2 text-white/50">PAN <span className="font-mono tracking-wider text-brand-cyan">{kycDialog?.pan_number}</span></p>
          </div>
          <form onSubmit={submitKycReview} className="mt-4 space-y-3">
            <label className="block text-xs font-semibold uppercase tracking-wider text-white/50">{kycDialog?.decision === "rejected" ? "Reason for rejection" : "Review note (optional)"}
              <textarea required={kycDialog?.decision === "rejected"} maxLength={300} rows={3} value={kycReviewNote} onChange={(e) => setKycReviewNote(e.target.value)} className={darkInput + " h-auto py-3"} placeholder={kycDialog?.decision === "rejected" ? "Tell the member what needs correction" : "Optional admin note"} />
            </label>
            <button type="submit" disabled={busy} className={`w-full h-11 rounded-full text-sm font-semibold disabled:opacity-50 ${kycDialog?.decision === "approved" ? "bg-emerald-500 text-slate-950" : "bg-rose-500 text-white"}`}>
              {busy ? "Saving…" : kycDialog?.decision === "approved" ? "Confirm approval" : "Confirm rejection"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
